import { fromString, toString, type MonetaryValue } from 'ezmoney';

import { type PeriodInterval } from '../../models/periodInterval';
import { transformDimensionTree } from '../helpers/budgetTree';
import {
  convertDimensionFromApi,
  convertDimensionToApi,
} from '../helpers/convertDimension';
import {
  convertDimensionValueFromApi,
  convertDimensionValueToApi,
} from '../helpers/convertDimensionValue';
import { type ApiBudgetTree, type BudgetTree } from '../models/budgetTree';
import { type BranchOf, type NodeOf } from '../models/dimensionTree';
import { buildApiPeriod, buildPeriod } from '../models/period';

export type BudgetDimension = 'costCenter' | 'expenseCategory' | 'supplier';

export type BudgetPeriodicity = 'monthly' | 'quarterly' | 'yearly' | 'overall';

type Outcome = {
  amounts: MonetaryValue[];
};

export type BudgetDefinitionTree = BudgetTree<Outcome>;
export type BudgetDefinitionBranch = BranchOf<BudgetDefinitionTree>;
export type BudgetDefinitionNode = NodeOf<BudgetDefinitionTree>;

export type BudgetDefinition = {
  id: string;
  companyId: string;
  name: string;
  currency: string;
  period: PeriodInterval;
  periodicity: BudgetPeriodicity;
  definition: BudgetDefinitionTree;
};

export type ApiBudgetDefinition = Omit<
  BudgetDefinition,
  'period' | 'definition'
> & {
  period: { start: string; end: string };
  definition: ApiDefinitionTree;
};

type ApiOutcome = {
  amounts: string[];
};

export type ApiDefinitionTree = ApiBudgetTree<ApiOutcome>;
export type ApiDefinitionBranch = BranchOf<ApiDefinitionTree>;
export type ApiDefinitionNode = NodeOf<ApiDefinitionTree>;

export const convertApiToBudgetDefinition = (
  apiDefinition: ApiBudgetDefinition,
): BudgetDefinition => ({
  id: apiDefinition.id,
  companyId: apiDefinition.companyId,
  name: apiDefinition.name,
  currency: apiDefinition.currency,
  period: buildPeriod(apiDefinition.period),
  periodicity: apiDefinition.periodicity,
  definition: transformDimensionTree(apiDefinition.definition, {
    transformDimension: convertDimensionFromApi,
    transformValues: convertDimensionValueFromApi,
    transformOutcome: (outcome): Outcome => ({
      amounts: outcome.amounts.map((amount) => fromString(amount)),
    }),
  }),
});

export const convertBudgetDefinitionTreeToApi = (
  definitionTree: BudgetDefinitionTree,
): ApiDefinitionTree =>
  transformDimensionTree(definitionTree, {
    transformDimension: convertDimensionToApi,
    transformValues: convertDimensionValueToApi,
    transformOutcome: (outcome): ApiOutcome => ({
      amounts: outcome.amounts.map((amount) => toString(amount)),
    }),
  });

export const convertBudgetDefinitionToApi = (
  budgetDefinition: BudgetDefinition,
): ApiBudgetDefinition => ({
  id: budgetDefinition.id,
  companyId: budgetDefinition.companyId,
  name: budgetDefinition.name,
  currency: budgetDefinition.currency,
  period: buildApiPeriod(budgetDefinition.period),
  periodicity: budgetDefinition.periodicity,
  definition: convertBudgetDefinitionTreeToApi(budgetDefinition.definition),
});
